import { Component } from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';

import constants from './Constants';

/* Components */
import EquationSolution from '../solutions/EquationSolution';
import EquationEditor from './editor/EquationEditor';

const getInputHeight = (numberOfLines) =>
  // If number of lines is undefined, 0 or negative, use 5.
  constants.inline.LINE_HEIGHT * (numberOfLines || 5);

class EquationEditorInline extends Component {
  constructor(props) {
    super(props);

    // Debounced event handlers
    this.handleEquationChange = _.debounce(this.handleEquationChange, 100);
    if (this.props.debounceAutoSave) {
      this.handleAutoSave = _.debounce(this.handleAutoSave, 2000);
    }
  }

  state = {
    equations: this.props.equations || [''],
    activeEquation: 0,
    minHeight: getInputHeight(this.props.numberOfLines),
  };

  handleAutoSave = () => {
    this.handleSave();
  };

  handleClick = () => {
    if (this.props.isActive) {
      return;
    }
    this.props.onClick();
  };

  handleEquationChange = (value) => {
    const equations = this.state.equations;

    // Update the active equation string
    equations[this.state.activeEquation] = value;

    this.setState(
      {
        equations,
      },
      this.handleAutoSave
    );
  };

  handleEquationInputClick = (index) => {
    if (index !== this.state.activeEquation) {
      this.setState({
        activeEquation: index,
      });
    }
  };

  handleEquationNavDown = () => {
    const equations = this.state.equations;

    // Add a new equation
    if (
      this.isLastEquationActive() &&
      !this.isActiveEquationEmpty() &&
      this.props.allowAddingLines
    ) {
      equations.push('');
    }

    this.setState({
      activeEquation: this.nextEquation(),
      equations,
    });
  };

  handleEquationNavUp = () => {
    let equations = this.state.equations;

    // Delete if last equation and is empty
    if (
      this.isLastEquationActive() &&
      this.isActiveEquationEmpty() &&
      this.props.allowAddingLines
    ) {
      equations = _.initial(equations);
    }

    this.setState({
      activeEquation: this.prevEquation(),
      equations,
    });
  };

  handleSave = () => {
    this.props.onSave({
      equations: this.state.equations,
    });
  };

  isActiveEquationEmpty = () => {
    return _.isEmpty(this.state.equations[this.state.activeEquation]);
  };

  isLastEquationActive = () => {
    return this.state.activeEquation === this.state.equations.length - 1;
  };

  nextEquation = () => {
    return Math.min(
      this.state.activeEquation + 1,
      this.state.equations.length - 1
    );
  };

  prevEquation = () => {
    return Math.max(this.state.activeEquation - 1, 0);
  };

  renderEditor = () => {
    return (
      <div>
        <div className="overlay" onClick={this.props.onClose} />
        <EquationEditor
          equations={this.state.equations}
          activeEquation={this.state.activeEquation}
          keypadLayout={this.props.keypadLayout}
          onInputClick={this.handleEquationInputClick}
          onChange={this.handleEquationChange}
          onClose={this.props.onClose}
          onNavUp={this.handleEquationNavUp}
          onNavDown={this.handleEquationNavDown}
          showNavWhenActive={this.props.showNavWhenActive}
        />
      </div>
    );
  };

  renderEditorReadOnly = () => {
    return (
      <div className="lined-paper" style={{ minHeight: this.state.minHeight }}>
        <EquationSolution equations={this.state.equations} />
      </div>
    );
  };

  render() {
    let editor;

    if (!this.props.isActive) {
      editor = this.renderEditorReadOnly();
    } else {
      editor = this.renderEditor();
    }

    const classes = classNames({
      'equation-editor-inline': true,
      active: this.props.isActive,
    });

    return (
      <div className={classes} onClick={this.handleClick}>
        {editor}
      </div>
    );
  }
}

EquationEditorInline.propTypes = {
  allowAddingLines: PropTypes.bool,
  equations: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool,
  keypadLayout: PropTypes.string,
  numberOfLines: PropTypes.number,
  onClose: PropTypes.func,
  onClick: PropTypes.func,
  onSave: PropTypes.func,
  showNavWhenActive: PropTypes.bool,
  debounceAutoSave: PropTypes.bool,
};

EquationEditorInline.defaultProps = {
  allowAddingLines: true,
  showNavWhenActive: true,
  debounceAutoSave: true,
};

export default EquationEditorInline;
